import React, { useState, useEffect, useMemo } from "react";
import "./projectShowcase.css";
import ShowcaseCard from "./ShowcaseCard";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import CollectionsBookmarkOutlinedIcon from "@mui/icons-material/CollectionsBookmarkOutlined";

const API = "https://hr-backend-72v1.onrender.com";

// Read-only browse + search of the Project Showcase. Used by sales reps
// (Employee + salesRep flag, in the Employee portal) and by Executives (in the
// HR container). The backend scopes results by the caller's real role/flags,
// so we simply fetch what `email` is allowed to see.
const ProjectShowcaseBrowse = ({ user }) => {
  const [entries, setEntries] = useState([]);
  const [managerList, setManagerList] = useState([]); // all Team Leads
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [tag, setTag] = useState("");
  const [manager, setManager] = useState("");

  useEffect(() => {
    const fetchEntries = async () => {
      setLoading(true);
      try {
        const res = await fetch(
          `${API}/project-showcase?email=${encodeURIComponent(user.email)}`
        );
        const data = await res.json();
        setEntries(Array.isArray(data) ? data : []);
      } catch (e) {
        setEntries([]);
      }
      setLoading(false);
    };
    fetchEntries();
  }, [user.email]);

  // Load every Team Lead so the Manager filter lists all of them - not only
  // those who happen to have uploaded a project yet.
  useEffect(() => {
    const fetchManagers = async () => {
      try {
        const res = await fetch(`${API}/employees-with-manager`);
        const data = await res.json();
        setManagerList(Array.isArray(data) ? data : []);
      } catch (e) {
        setManagerList([]);
      }
    };
    fetchManagers();
  }, []);

  const allTags = useMemo(() => {
    const s = new Set();
    entries.forEach((e) => (e.services || []).forEach((t) => s.add(t)));
    return Array.from(s).sort();
  }, [entries]);

  // Managers shown in the filter: every current Team Lead, plus any project
  // owner not in that list (e.g. someone who has since been un-managered) so
  // every visible project stays filterable.
  const allManagers = useMemo(() => {
    const m = new Map();
    managerList.forEach((mgr) => {
      if (mgr.email && !m.has(mgr.email)) {
        m.set(mgr.email, String(mgr.name || mgr.email));
      }
    });
    entries.forEach((e) => {
      if (e.ownerEmail && !m.has(e.ownerEmail)) {
        m.set(e.ownerEmail, String(e.ownerName || e.ownerEmail));
      }
    });
    return Array.from(m, ([email, name]) => ({ email, name })).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }, [managerList, entries]);

  const filtered = useMemo(() => {
    return entries.filter((e) => {
      if (tag && !(e.services || []).includes(tag)) return false;
      if (manager && e.ownerEmail !== manager) return false;
      if (q) {
        const hay = `${e.title} ${e.clientName || ""} ${e.description || ""}`.toLowerCase();
        if (!hay.includes(q.toLowerCase())) return false;
      }
      return true;
    });
  }, [entries, q, tag, manager]);

  return (
    <div className="psWrap">
      <header className="psHeader">
        <div>
          <h1 className="psTitle">Project Showcase</h1>
          <p className="psSubtitle">
            Browse and search past projects to answer client questions instantly
          </p>
        </div>
      </header>

      <div className="psToolbar">
        <div className="psFilter psFilter--grow">
          <span className="psFilterLabel">Search</span>
          <div className="psSearch">
            <SearchOutlinedIcon fontSize="small" />
            <input
              type="text"
              placeholder="Search by title, client or keyword…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
        </div>
        <div className="psFilter">
          <span className="psFilterLabel">Manager</span>
          <select
            className="psSelect"
            value={manager}
            onChange={(e) => setManager(e.target.value)}
          >
            <option value="">All managers</option>
            {allManagers.map((m) => (
              <option key={m.email} value={m.email}>
                {m.name}
              </option>
            ))}
          </select>
        </div>
        <div className="psFilter">
          <span className="psFilterLabel">Service</span>
          <select
            className="psSelect"
            value={tag}
            onChange={(e) => setTag(e.target.value)}
          >
            <option value="">All services</option>
            {allTags.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="psEmpty">
          <div className="psEmptySub">Loading projects…</div>
        </div>
      ) : filtered.length > 0 ? (
        <div className="psGrid">
          {filtered.map((e) => (
            <ShowcaseCard key={e._id} entry={e} />
          ))}
        </div>
      ) : (
        <div className="psEmpty">
          <span className="psEmptyIcon">
            <CollectionsBookmarkOutlinedIcon />
          </span>
          <div>
            <div className="psEmptyTitle">
              {entries.length === 0
                ? "No projects yet"
                : "No projects match your filters"}
            </div>
            <div className="psEmptySub">
              {entries.length === 0
                ? "Team leads will add delivered projects here."
                : "Try clearing the search or filters above."}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectShowcaseBrowse;
